/**
 * Spec Phase 4 Slice 4E — ReferenceSignsDock.
 * Phase 7 Slice 7F — auto-scroll + flash on numeral:click.
 *
 * Lists every reference sign derived from drawing_numerals + claim
 * term mappings. Source classification ('drawing' / 'both') and
 * appears_in_figures come from the spec store's referenceSigns
 * field, populated at hydrate time via derive_reference_signs.
 *
 * Phase 7 Slice 7F: subscribes to numeralEventBus. On click,
 * the row scrolls into view (if the dock is open; if it's
 * closed we open it first), then flashes blue for ~1.2 s so
 * the drafter's eye latches on.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Hash } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import { subscribe, type NumeralEvent } from '@/lib/numeralEventBus';
import Dock from './Dock';

export default function ReferenceSignsDock(): JSX.Element {
  const referenceSigns = useSpecStore((s) => s.referenceSigns);
  const setDockOpen = useSpecStore((s) => s.setDockOpen);
  const rowRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const [flashNumeral, setFlashNumeral] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribe((ev: NumeralEvent) => {
      if (ev.kind !== 'numeral:click') return;
      // Defined numerals jump-to-row. Undefined numerals belong
      // to the Examiner dock — let that dock handle them.
      if (ev.undefined_) return;
      setDockOpen('referenceSigns', true);
      // Defer the scroll one frame so the <details> open
      // animation has a chance to lay out the rows before we
      // call scrollIntoView (otherwise the offset is wrong).
      window.requestAnimationFrame(() => {
        const el = rowRefs.current.get(ev.numeral);
        if (!el) return;
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
        setFlashNumeral(ev.numeral);
        window.setTimeout(() => {
          setFlashNumeral((cur) => (cur === ev.numeral ? null : cur));
        }, 1200);
      });
    });
    return () => {
      unsub();
    };
  }, [setDockOpen]);

  return (
    <Dock
      dockKey="referenceSigns"
      icon={<Hash className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Reference signs"
      pillCount={referenceSigns.length}
      meta={
        referenceSigns.length === 0
          ? 'Reference signs derive from frozen drawings + frozen claims.'
          : undefined
      }
      testId="spec-dock-reference-signs"
    >
      {referenceSigns.length === 0 ? null : (
        <ul className="space-y-1">
          {referenceSigns.map((r) => (
            <li
              key={r.numeral}
              ref={(el) => {
                if (el) rowRefs.current.set(r.numeral, el);
                else rowRefs.current.delete(r.numeral);
              }}
              className={[
                'flex items-start gap-2 rounded-sm bg-white px-2 py-1',
                flashNumeral === r.numeral ? 's6-row-flash' : '',
              ].join(' ')}
              data-testid={`spec-reference-sign-${r.numeral}`}
            >
              <span className="s6-num shrink-0" data-num={r.numeral}>
                {r.numeral}
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-[11px] text-zinc-700">{r.label}</span>
                {r.appears_in_figures.length > 0 && (
                  <span className="ml-1 font-mono text-[10px] text-zinc-400">
                    · {r.appears_in_figures.join(', ')}
                  </span>
                )}
              </div>
              <span
                className={[
                  'shrink-0 rounded-sm px-1 font-mono text-[9px]',
                  r.source === 'both'
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-zinc-100 text-zinc-600',
                ].join(' ')}
              >
                {r.source}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dock>
  );
}
